import type { ReactNode } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { CARD_SHADOW, COLORS } from '../../IndividualPlan/tokens';

export type GenerationPanelProps = {
  phase: 'idle' | 'submitting' | 'generating' | 'ready' | 'failed' | 'error';
  errorClass: string | null;
  onGenerate: () => void;
  onRetry: () => void;
};

const FAILURE_HINTS: Record<string, string> = {
  generation_timeout: 'It ran out of time.',
  llm_unavailable: 'The writing service was unavailable.',
};

const REJECTION_HINTS: Record<string, string> = {
  district_identity_unknown:
    'No district name or state is on file for this account, so the report cannot be labelled.',
  no_uploaded_data: 'No ACCESS upload was found for this district.',
  feed_incomplete: 'The roster feed is missing program dates.',
  school_year_mismatch: 'The data on file is for a different school year.',
  uploads_changed: 'The district data changed while the report was being assembled. Try again.',
  access_results_required: 'Upload your ACCESS results first.',
  sis_connection_required: 'Connect your SIS first; an uploaded roster file does not count.',
  worker_not_started: 'The run was never picked up. Try again to start a new one.',
  generation_stalled: 'The run stopped responding. Try again to pick it up where it left off.',
};

function withHint(base: string, hints: Record<string, string>, errorClass: string | null): string {
  const hint = errorClass === null ? undefined : hints[errorClass];
  return hint ? `${base} ${hint}` : base;
}

function PanelRoot({ children }: { children: ReactNode }) {
  return (
    <Box
      data-testid="generation-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        padding: '24px',
        marginBottom: '20px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: CARD_SHADOW,
      }}
    >
      {children}
    </Box>
  );
}

function FailureAlert({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <PanelRoot>
      <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
        {message}
      </Typography>
      <Button variant="contained" onClick={onRetry}>
        Try again
      </Button>
    </PanelRoot>
  );
}

export function RegeneratePanel({
  onRegenerate,
  plansGenerated,
  spacingPosition = 'top',
}: {
  onRegenerate: () => void;
  plansGenerated: boolean;
  spacingPosition?: 'top' | 'bottom';
}) {
  return (
    <Box
      data-testid="regenerate-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        padding: '24px',
        [spacingPosition === 'top' ? 'marginTop' : 'marginBottom']: '20px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: CARD_SHADOW,
      }}
    >
      <Typography sx={{ fontSize: 16, color: COLORS.muted, lineHeight: 1.6 }}>
        {plansGenerated
          ? 'Uploaded new data? Regenerate this report and refresh your Student Action Plans.'
          : 'Uploaded new data? Regenerate this report.'}
      </Typography>
      <Button variant="outlined" onClick={onRegenerate}>
        {plansGenerated ? 'Regenerate reports & plans' : 'Regenerate report'}
      </Button>
    </Box>
  );
}

export function GenerationPanel({ phase, errorClass, onGenerate, onRetry }: GenerationPanelProps) {
  if (phase === 'ready') {
    return null;
  }

  if (phase === 'idle' || phase === 'submitting') {
    const submitting = phase === 'submitting';
    return (
      <PanelRoot>
        <Typography sx={{ fontSize: 16, color: COLORS.muted, lineHeight: 1.6 }}>
          {"Generate this year's Program Evaluation from your uploaded district data."}
        </Typography>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={onGenerate}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Submitting…' : 'Generate Report'}
        </Button>
      </PanelRoot>
    );
  }

  if (phase === 'generating') {
    return (
      <PanelRoot>
        <CircularProgress aria-label="Generating Program Evaluation report" />
        <Typography sx={{ fontSize: 16, color: COLORS.muted, lineHeight: 1.6 }}>
          {'Generating your Program Evaluation — this can take several minutes. ' +
            'You can leave this page and come back.'}
        </Typography>
      </PanelRoot>
    );
  }

  if (phase === 'failed') {
    const message = withHint("Generation didn't finish.", FAILURE_HINTS, errorClass);
    return <FailureAlert message={message} onRetry={onRetry} />;
  }

  const message = withHint(
    'Something went wrong starting the generation.',
    REJECTION_HINTS,
    errorClass,
  );
  return <FailureAlert message={message} onRetry={onRetry} />;
}
